import { Edge, Node } from 'reactflow';

import { CustomEdge } from '@hooks/useCanvasEdges';

import { CanvasResponse } from '@models/Canvas';

type MergeResult = {
    nodes: Node[];
    edges: Edge<CustomEdge[]>[];
    deleted_nodes: Node[];
    deleted_edges: Edge[];
    hasChanges: boolean;
};

/**
 * Get the metadata from a node or edge
 * @param obj The node or edge to get metadata from
 * @returns The metadata or null if not present
 */
export const getMetadata = (obj: any): { created_at?: number; updated_at?: number; user_uuid?: string } | null => {
    return obj?.data?.metadata || null;
};

/**
 * Compare two objects based on their updated_at timestamp
 * @param obj1 First object
 * @param obj2 Second object
 * @returns true if obj1 is more recent than obj2, false otherwise
 */
export const isMoreRecent = (obj1: any, obj2: any): boolean => {
    const meta1 = getMetadata(obj1);
    const meta2 = getMetadata(obj2);

    // If either object doesn't have metadata, prefer the one with metadata
    if (!meta1) return false;
    if (!meta2) return true;

    // If both have metadata, compare updated_at timestamps
    if (meta1.updated_at && meta2.updated_at) {
        return meta1.updated_at > meta2.updated_at;
    }

    // If no updated_at, fall back to created_at
    if (meta1.created_at && meta2.created_at) {
        return meta1.created_at > meta2.created_at;
    }

    // If no timestamps, default to false (prefer obj2)
    return false;
};

/**
 * Merge the origin canvas data with the current canvas data
 */
export const mergeCanvasData = (originData: CanvasResponse | null, currentData: CanvasResponse | null): MergeResult => {
    if (!originData || !currentData) {
        return {
            nodes: currentData?.nodes || [],
            edges: currentData?.edges || [],
            deleted_nodes: currentData?.deleted_nodes || [],
            deleted_edges: currentData?.deleted_edges || [],
            hasChanges: false,
        };
    }

    const currentNodes = currentData.nodes || [];
    const currentEdges = currentData.edges || [];
    const currentDeletedNodes = currentData.deleted_nodes || [];
    const currentDeletedEdges = currentData.deleted_edges || [];

    // Create maps for deleted nodes and edges for faster lookups
    const deletedNodeIdsMap = new Map<string, Node>();
    const deletedEdgeIdsMap = new Map<string, Edge>();
    let hasChanges = false;

    // Populate deleted maps from both origin and current
    (originData.deleted_nodes || []).forEach((node) => {
        deletedNodeIdsMap.set(node.id, node);
    });

    currentDeletedNodes.forEach((node) => {
        deletedNodeIdsMap.set(node.id, node);
    });

    (originData.deleted_edges || []).forEach((edge) => {
        deletedEdgeIdsMap.set(edge.id, edge);
    });

    currentDeletedEdges.forEach((edge) => {
        deletedEdgeIdsMap.set(edge.id, edge);
    });

    // Get arrays of deleted IDs for filtering
    const deletedNodeIds = Array.from(deletedNodeIdsMap.keys());
    const deletedEdgeIds = Array.from(deletedEdgeIdsMap.keys());

    // Check if any current nodes or edges are in the deleted lists from the API
    // If so, they need to be removed from the current state
    const newCurrentNodes = currentNodes.filter((node) => {
        // If this node is in the deleted list from the API but not in our current deleted list,
        // we need to remove it and mark that we have changes
        const isDeleted = deletedNodeIds.includes(node.id);
        const isAlreadyDeleted = currentDeletedNodes.some((n) => n.id === node.id);

        if (isDeleted && !isAlreadyDeleted) {
            hasChanges = true;
            return false; // Filter it out
        }
        return true; // Keep it
    });

    const newCurrentEdges = currentEdges.filter((edge) => {
        // If this edge is in the deleted list from the API but not in our current deleted list,
        // we need to remove it and mark that we have changes
        const isDeleted = deletedEdgeIds.includes(edge.id);
        const isAlreadyDeleted = currentDeletedEdges.some((e) => e.id === edge.id);

        if (isDeleted && !isAlreadyDeleted) {
            hasChanges = true;
            return false; // Filter it out
        }
        return true; // Keep it
    });

    // Create maps for faster lookups
    const originNodesMap = new Map<string, Node>();
    const currentNodesMap = new Map<string, Node>();
    const originEdgesMap = new Map<string, Edge>();
    const currentEdgesMap = new Map<string, Edge>();

    // Populate maps, filtering out deleted elements
    originData.nodes?.forEach((node) => {
        if (!deletedNodeIds.includes(node.id)) {
            originNodesMap.set(node.id, node);
        }
    });

    newCurrentNodes.forEach((node) => {
        if (!deletedNodeIds.includes(node.id)) {
            currentNodesMap.set(node.id, node);
        }
    });

    originData.edges?.forEach((edge) => {
        if (!deletedEdgeIds.includes(edge.id)) {
            originEdgesMap.set(edge.id, edge);
        }
    });

    newCurrentEdges.forEach((edge) => {
        if (!deletedEdgeIds.includes(edge.id)) {
            currentEdgesMap.set(edge.id, edge);
        }
    });

    // Continue tracking if any changes were made during the merge

    // Merge nodes
    const mergedNodes: Node[] = [];
    const allNodeIds = new Set([...originNodesMap.keys(), ...currentNodesMap.keys()]);

    // Process all node IDs
    allNodeIds.forEach((id) => {
        const originNode = originNodesMap.get(id);
        const currentNode = currentNodesMap.get(id);

        // If node exists only in origin
        if (originNode && !currentNode) {
            mergedNodes.push(originNode);
            hasChanges = true;
            return;
        }

        // If node exists only in current
        if (!originNode && currentNode) {
            mergedNodes.push(currentNode);
            return;
        }

        // If node exists in both, determine which is more recent
        if (originNode && currentNode) {
            if (isMoreRecent(currentNode, originNode)) {
                mergedNodes.push(currentNode);
            } else if (isMoreRecent(originNode, currentNode)) {
                // Extract UI states we want to preserve from current node
                const uiState = {
                    selected: currentNode.selected,
                    dragging: currentNode.dragging,
                    resizing: currentNode.resizing,
                };
                // Merge origin node with preserved UI states
                mergedNodes.push({
                    ...originNode,
                    ...uiState,
                });
                hasChanges = true;
            } else {
                // If timestamps are equal, use current node
                mergedNodes.push(currentNode);
            }
        }
    });

    // Merge edges
    const mergedEdges: Edge<CustomEdge[]>[] = [];
    const allEdgeIds = new Set([...originEdgesMap.keys(), ...currentEdgesMap.keys()]);
    const nodeIdSet = new Set(mergedNodes.map((node) => node.id));

    // Process all edge IDs
    allEdgeIds.forEach((id) => {
        const originEdge = originEdgesMap.get(id);
        const currentEdge = currentEdgesMap.get(id);

        // Helper function to check if an edge's source and target nodes exist
        const edgeNodesExist = (edge: Edge): boolean => {
            return nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target);
        };

        // If edge exists only in origin and its nodes exist
        if (originEdge && !currentEdge && edgeNodesExist(originEdge)) {
            mergedEdges.push(originEdge as Edge<CustomEdge[]>);
            hasChanges = true;
            return;
        }

        // If edge exists only in current and its nodes exist
        if (!originEdge && currentEdge && edgeNodesExist(currentEdge)) {
            mergedEdges.push(currentEdge as Edge<CustomEdge[]>);
            return;
        }

        // If edge exists in both, determine which is more recent
        if (originEdge && currentEdge) {
            if (isMoreRecent(currentEdge, originEdge)) {
                // Current edge is more recent
                mergedEdges.push(currentEdge as Edge<CustomEdge[]>);
            } else if (isMoreRecent(originEdge, currentEdge)) {
                // Origin edge is more recent
                mergedEdges.push(originEdge as Edge<CustomEdge[]>);
                hasChanges = true;
            } else {
                // If timestamps are equal, use current edge
                mergedEdges.push(currentEdge as Edge<CustomEdge[]>);
            }

            // Only add if both source and target nodes exist
            if (!edgeNodesExist(mergedEdges[mergedEdges.length - 1])) {
                mergedEdges.pop();
            }
        }
    });

    return {
        nodes: mergedNodes,
        edges: mergedEdges,
        deleted_nodes: Array.from(deletedNodeIdsMap.values()),
        deleted_edges: Array.from(deletedEdgeIdsMap.values()),
        hasChanges,
    };
};
